import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ShieldCheck, CheckCircle2, XCircle, FileText } from "lucide-react";
import { API, BACKEND, fmtTgl } from "../lib/api";

export default function Verifikasi() {
  const { nomor } = useParams();
  const nav = useNavigate();
  const [mode, setMode] = useState("satu");
  const [input, setInput] = useState(nomor || "");
  const [banyak, setBanyak] = useState("");
  const [hasil, setHasil] = useState(null);
  const [massal, setMassal] = useState(null);
  const [muat, setMuat] = useState(false);

  const periksa = async (n) => {
    if (!n.trim()) return;
    setMuat(true); setHasil(null);
    try {
      const r = await API.get(`/verifikasi/${encodeURIComponent(n.trim())}`);
      setHasil(r.data);
    } catch { setHasil({ status: "TIDAK DITEMUKAN", nomor: n }); }
    finally { setMuat(false); }
  };

  const periksaBanyak = async (e) => {
    e.preventDefault();
    const list = banyak.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);
    if (!list.length) { setMassal(null); return; }
    setMuat(true); setMassal(null);
    try {
      const r = await API.post("/verifikasi/massal", { nomor_list: list });
      setMassal(r.data);
    } catch { setMassal({ jumlah: 0, sah: 0, hasil: [] }); }
    finally { setMuat(false); }
  };

  useEffect(() => { if (nomor) periksa(nomor); }, [nomor]);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="kaca border-b sticky top-0 z-20">
        <div className="max-w-[1000px] mx-auto px-4 md:px-8 h-14 flex items-center justify-between">
          <Link to="/" data-testid="tautan-beranda" className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-primary" aria-hidden="true" />
            <span className="font-heading font-bold text-lg">PAM-IT</span>
          </Link>
          <Link to="/demo" className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-150">
            Data contoh
          </Link>
        </div>
      </header>

      <main className="flex-1 px-4 md:px-8 py-16 max-w-[1000px] w-full mx-auto">
        <p className="label-kecil">Pemeriksaan publik</p>
        <h1 className="mt-4 text-4xl sm:text-5xl font-bold" data-testid="judul-verifikasi">Cek keaslian surat</h1>
        <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">
          Masukkan nomor Surat Keterangan Bebas Akses. Kami hanya menampilkan nomor, tanggal terbit, jumlah akses,
          dan sidik digitalnya — tidak ada data pribadi yang dibuka di sini.
        </p>

        <div className="mt-8 flex gap-2" role="tablist">
          {[["satu", "Satu surat"], ["massal", "Banyak surat sekaligus"]].map(([k, l]) => (
            <button key={k} role="tab" aria-selected={mode === k} data-testid={`tab-${k}`}
              onClick={() => setMode(k)}
              className={`px-4 py-2 text-sm rounded transition-colors duration-150 ${mode === k ? "bg-primary text-primary-foreground font-semibold" : "tombol-garis"}`}>
              {l}
            </button>
          ))}
        </div>

        {mode === "satu" ? (
          <>
            <form className="mt-6 flex flex-col sm:flex-row gap-2 max-w-xl"
              onSubmit={(e) => { e.preventDefault(); nav(`/verifikasi/${encodeURIComponent(input.trim())}`); periksa(input); }}>
              <input data-testid="input-nomor-verifikasi" value={input} onChange={(e) => setInput(e.target.value)}
                placeholder="SKBA-2026-0001" aria-label="Nomor surat"
                className="flex-1 border rounded px-3 py-2.5 text-sm font-data" />
              <button data-testid="btn-periksa" disabled={muat} className="tombol-utama px-5 py-2.5 rounded text-sm disabled:opacity-50">
                {muat ? "Memeriksa…" : "Periksa"}
              </button>
            </form>

            {hasil && (
              <div data-testid="hasil-verifikasi"
                className={`mt-10 panel rounded p-6 md:p-8 border-l-4 ${hasil.status === "SAH" ? "border-l-primary" : "border-l-destructive"}`}>
                <div className="flex items-center gap-3">
                  {hasil.status === "SAH"
                    ? <CheckCircle2 size={28} className="text-primary" aria-hidden="true" />
                    : <XCircle size={28} className="text-destructive" aria-hidden="true" />}
                  <p data-testid="status-verifikasi"
                    className={`text-3xl font-bold font-heading ${hasil.status === "SAH" ? "text-primary" : "text-destructive"}`}>
                    {hasil.status === "SAH" ? "SAH" : "TIDAK DITEMUKAN"}
                  </p>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">
                  {hasil.status === "SAH"
                    ? "Surat ini benar terbit dari PAM-IT dan isinya belum berubah."
                    : "Tidak ada surat dengan nomor itu. Periksa lagi penulisannya, atau surat itu memang bukan dari sini."}
                </p>
                <dl className="mt-6 divide-y border-t">
                  <div className="flex justify-between py-2.5 text-sm">
                    <dt className="text-muted-foreground">Nomor surat</dt>
                    <dd className="font-data" data-testid="v-nomor">{hasil.nomor}</dd>
                  </div>
                  {hasil.status === "SAH" && (
                    <>
                      <div className="flex justify-between py-2.5 text-sm">
                        <dt className="text-muted-foreground">Tanggal terbit</dt>
                        <dd className="font-data" data-testid="v-tanggal">{fmtTgl(hasil.tanggal)}</dd>
                      </div>
                      <div className="flex justify-between py-2.5 text-sm">
                        <dt className="text-muted-foreground">Jumlah akses yang ditutup</dt>
                        <dd className="font-data" data-testid="v-jumlah">{hasil.jumlah_sistem}</dd>
                      </div>
                      <div className="flex flex-wrap justify-between gap-2 py-2.5 text-sm">
                        <dt className="text-muted-foreground">Sidik digital</dt>
                        <dd className="font-data text-xs break-all text-accent" data-testid="v-sidik">{hasil.sidik}</dd>
                      </div>
                    </>
                  )}
                </dl>
                {hasil.status === "SAH" && hasil.token && (
                  <a data-testid="tautan-pdf-verifikasi" target="_blank" rel="noreferrer"
                    href={`${BACKEND}/api/surat/pdf/${hasil.token}`}
                    className="tombol-garis mt-6 inline-flex items-center gap-2 px-4 py-2 rounded text-sm">
                    <FileText size={15} /> Buka surat aslinya (PDF)
                  </a>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <form className="mt-6 max-w-xl" onSubmit={periksaBanyak}>
              <label htmlFor="banyak" className="label-kecil">Tempel nomor surat, satu per baris</label>
              <textarea id="banyak" data-testid="input-massal" rows={6} value={banyak}
                onChange={(e) => setBanyak(e.target.value)}
                placeholder={"SKBA-2026-0001\nSKBA-2026-0002"}
                className="mt-2 w-full border rounded px-3 py-2.5 text-sm font-data" />
              <button data-testid="btn-periksa-massal" disabled={muat} className="tombol-utama mt-3 px-5 py-2.5 rounded text-sm disabled:opacity-50">
                {muat ? "Memeriksa…" : "Periksa semuanya"}
              </button>
            </form>

            {massal && (
              <div className="mt-10" data-testid="hasil-massal">
                <p className="text-sm text-muted-foreground">
                  <span className="font-data font-bold text-foreground">{massal.sah}</span> dari{" "}
                  <span className="font-data font-bold text-foreground">{massal.jumlah}</span> nomor terbukti sah.
                </p>
                <div className="mt-4 panel rounded divide-y">
                  {massal.hasil.map((h) => (
                    <div key={h.nomor} data-testid={`massal-${h.nomor}`}
                      className="flex flex-wrap items-center justify-between gap-2 p-3 text-sm">
                      <span className="font-data">{h.nomor}</span>
                      <span className={`inline-flex items-center gap-2 font-semibold ${h.status === "SAH" ? "text-primary" : "text-destructive"}`}>
                        {h.status === "SAH" ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                        {h.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <footer className="border-t px-4 md:px-8 py-6 text-xs text-muted-foreground">
        Halaman ini terbuka untuk umum. Tidak perlu akun untuk memeriksa surat.
      </footer>
    </div>
  );
}
